import { useRef, useState } from "react";
import { extractErrorMessage } from "@/core/api/errors";
import { isOnboardingConflict } from "./onboarding-api";
import { useUploadInvoiceSample } from "./shared-query";
import {
  UPLOAD_ITEM_STATE,
  type UploadItem,
  type UploadItemState,
} from "./types";

export const useInvoiceSampleUpload = (businessId: string) => {
  const mutation = useUploadInvoiceSample(businessId);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const busy = useRef(false);

  const upload = async (files: File[], retainFailures = true) => {
    if (busy.current || !files.length) return;
    busy.current = true;
    setIsUploading(true);
    // Keep old failures when the customer adds another batch.
    const batch: UploadItem[] = [
      ...(retainFailures
        ? items.filter(
            ({ status }) => status.state === UPLOAD_ITEM_STATE.failed,
          )
        : []),
      ...files.map<UploadItem>((file) => ({
        file,
        status: { state: UPLOAD_ITEM_STATE.pending },
      })),
    ];
    setItems(batch);
    const update = (index: number, status: UploadItemState) =>
      setItems((current) =>
        current.map((item, i) => (i === index ? { ...item, status } : item)),
      );
    let closed = false;
    try {
      for (let i = 0; i < batch.length; i += 1) {
        if (batch[i].status.state === UPLOAD_ITEM_STATE.failed) continue;
        if (closed) {
          update(i, {
            state: UPLOAD_ITEM_STATE.failed,
            message:
              "Setup has changed. Return to the saved step before retrying.",
          });
          continue;
        }
        update(i, { state: UPLOAD_ITEM_STATE.uploading, progress: 0 });
        try {
          await mutation.mutateAsync({
            file: batch[i].file,
            onProgress: (progress) =>
              update(i, { state: UPLOAD_ITEM_STATE.uploading, progress }),
          });
          update(i, { state: UPLOAD_ITEM_STATE.done });
        } catch (error) {
          closed = isOnboardingConflict(error);
          update(i, {
            state: UPLOAD_ITEM_STATE.failed,
            message: extractErrorMessage(error).message,
          });
        }
      }
      setItems((current) =>
        current.filter(
          ({ status }) => status.state === UPLOAD_ITEM_STATE.failed,
        ),
      );
    } finally {
      busy.current = false;
      setIsUploading(false);
    }
  };

  // Retry only failures, without retaining a second copy of the batch.
  const retry = () =>
    upload(
      items.map(({ file }) => file),
      false,
    );

  return { items, isUploading, upload, retry };
};
